import { useEffect, useState } from "react";
import { BookOpen, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ScopeToggle } from "./shared";
import { Button } from "@/components/ui/button";

export default function Education({ scope, setScope }) {
  const [curated, setCurated] = useState([]);
  const [aiTips, setAiTips] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/tips/curated").then(({ data }) => setCurated(data)); }, []);

  const generate = async () => {
    setLoading(true); setAiTips([]);
    try {
      const { data } = await api.post(`/tips/ai?scope=${scope}`);
      setAiTips(data.tips || []);
      if (!data.tips?.length) toast.info("Adicione transações para dicas mais personalizadas.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="education-section">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-text-primary">Educação Financeira</h2>
          <p className="text-text-secondary font-body mt-1">Sabedoria dos maiores bestsellers, aplicada à sua realidade.</p>
        </div>
        <ScopeToggle scope={scope} setScope={setScope} />
      </div>

      <div className="relative overflow-hidden rounded-md border border-stone-200">
        <img src="https://images.unsplash.com/photo-1604594849809-dfedbc827105?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBlZHVjYXRpb24lMjBib29rc3xlbnwwfHx8fDE3ODY0MDQxNjN8MA&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="relative p-8 lg:p-10 text-white flex items-center justify-between flex-wrap gap-6">
          <div className="max-w-lg">
            <h3 className="font-heading text-2xl font-bold tracking-tight">Dicas personalizadas com IA</h3>
            <p className="text-white/80 font-body mt-2 leading-relaxed">
              Gere conselhos sob medida analisando suas finanças {scope === "business" ? "empresariais" : "pessoais"}, com base nos princípios dos bestsellers.
            </p>
          </div>
          <Button data-testid="generate-ai-tips" onClick={generate} disabled={loading}
            className="rounded-md bg-white text-primary hover:bg-white/90 h-11">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {loading ? "Analisando..." : "Gerar dicas"}
          </Button>
        </div>
      </div>

      {aiTips.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="ai-tips-list">
          {aiTips.map((t, i) => (
            <div key={i} className="border border-primary/20 bg-primary/5 rounded-md p-6">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-4 w-4" />
                <p className="uppercase text-xs tracking-[0.2em] font-body">Dica IA</p>
              </div>
              <h4 className="font-heading text-lg font-bold tracking-tight text-text-primary mt-3">{t.title}</h4>
              <p className="text-text-secondary font-body mt-2 leading-relaxed text-sm">{t.tip}</p>
              {t.book && <p className="text-xs text-text-muted font-body mt-3 italic">Inspirado em: {t.book}</p>}
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 className="font-heading text-xl font-bold tracking-tight text-text-primary mb-6">Sabedoria dos bestsellers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {curated.map((t, i) => (
            <div key={i} data-testid={`curated-tip-${i}`}
              className="border border-stone-200 bg-white rounded-md p-6 hover:-translate-y-1 hover:shadow-sm transition-transform">
              <BookOpen className="h-5 w-5 text-secondary" />
              <p className="text-text-secondary font-body mt-4 leading-relaxed">{t.tip}</p>
              <p className="font-heading font-bold text-text-primary mt-4 text-sm">{t.book}</p>
              <p className="text-xs text-text-muted font-body">{t.author}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
